import { useState, useEffect, useRef } from 'react';
import { useOutletContext } from 'react-router-dom';
import API from '../lib/api';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Badge } from '../components/ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../components/ui/dialog';
import { Textarea } from '../components/ui/textarea';
import { Switch } from '../components/ui/switch';
import { toast } from 'sonner';
import { Plus, Trash, Play, Pause, PencilSimple, Image, VideoCamera, Link, X } from '@phosphor-icons/react';

const INTERVALS = [
  { value: 'hourly', label: 'Every Hour' },
  { value: 'daily', label: 'Daily' },
  { value: 'weekly', label: 'Weekly (Monday)' },
];
const TARGETS = [
  { value: 'all', label: 'All Users' },
  { value: 'active', label: 'Active Users Only' },
];

export default function AutoMessagesPage() {
  const { selectedBot, bots } = useOutletContext();
  const [msgs, setMsgs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [addOpen, setAddOpen] = useState(false);
  const [editId, setEditId] = useState(null);
  const [text, setText] = useState('');
  const [interval, setIntervalVal] = useState('daily');
  const [time, setTime] = useState('09:00');
  const [target, setTarget] = useState('all');
  const [mediaFile, setMediaFile] = useState(null);
  const [buttons, setButtons] = useState([]);
  const [showBtnLabel, setShowBtnLabel] = useState(true);
  const [saving, setSaving] = useState(false);
  const fileRef = useRef(null);

  const botId = selectedBot || (bots[0]?.id);

  const fetchMsgs = () => {
    const params = botId ? { bot_id: botId } : {};
    API.get('/auto-messages', { params }).then(r => setMsgs(r.data.auto_messages || [])).catch(() => {}).finally(() => setLoading(false));
  };
  useEffect(() => { fetchMsgs(); }, [botId]);

  const resetForm = () => { setEditId(null); setText(''); setIntervalVal('daily'); setTime('09:00'); setTarget('all'); setMediaFile(null); setButtons([]); setShowBtnLabel(true); };

  const handleSave = async (e) => {
    e.preventDefault();
    if (!text.trim() && !mediaFile && buttons.length === 0) return toast.error('Add some content');
    if (!botId) return toast.error('No bot selected');
    setSaving(true);
    try {
      if (editId) {
        await API.put(`/auto-messages/${editId}`, { text, interval, time, target, inline_buttons: buttons, show_button_label: showBtnLabel });
        toast.success('Updated');
      } else {
        const fd = new FormData();
        fd.append('bot_id', botId);
        if (text.trim()) fd.append('text', text.trim());
        fd.append('interval', interval);
        fd.append('time', time);
        fd.append('target', target);
        if (mediaFile) fd.append('media_file', mediaFile);
        if (buttons.length > 0) fd.append('inline_buttons', JSON.stringify(buttons));
        fd.append('show_button_label', showBtnLabel.toString());
        await API.post('/auto-messages', fd);
        toast.success('Auto-message created');
      }
      setAddOpen(false); resetForm(); fetchMsgs();
    } catch (err) { toast.error(err.response?.data?.detail || 'Failed'); }
    finally { setSaving(false); }
  };

  const toggleActive = async (msg) => {
    try { await API.put(`/auto-messages/${msg.id}`, { is_active: !msg.is_active }); fetchMsgs(); toast.success(msg.is_active ? 'Paused' : 'Activated'); } catch (e) { toast.error('Failed'); }
  };
  const deleteMsg = async (id) => {
    if (!window.confirm('Delete this auto-message?')) return;
    try { await API.delete(`/auto-messages/${id}`); toast.success('Deleted'); fetchMsgs(); } catch (e) { toast.error('Failed'); }
  };
  const openEdit = (m) => {
    setEditId(m.id); setText(m.text || ''); setIntervalVal(m.interval); setTime(m.time || '09:00');
    setTarget(m.target || 'all'); setButtons(m.inline_buttons || []); setShowBtnLabel(m.show_button_label !== false); setMediaFile(null); setAddOpen(true);
  };

  const botMap = {};
  bots.forEach(b => { botMap[b.id] = b; });

  return (
    <div className="space-y-6 max-w-3xl" data-testid="auto-messages-page">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl sm:text-3xl font-bold tracking-tight" style={{ fontFamily: 'Cabinet Grotesk' }}>Auto Messages</h1>
          <p className="text-sm text-muted-foreground mt-1">Recurring messages: Media → Text → Inline Buttons</p>
        </div>
        <Button onClick={() => { resetForm(); setAddOpen(true); }} className="gap-1.5" data-testid="add-auto-msg-btn">
          <Plus size={16} /> Add
        </Button>
      </div>

      {loading ? <p className="text-muted-foreground">Loading...</p> : msgs.length === 0 ? (
        <div className="border border-dashed border-border rounded-md p-8 sm:p-12 text-center text-muted-foreground">
          No auto-messages yet. Create one to start sending recurring messages.
        </div>
      ) : (
        <div className="space-y-3">
          {msgs.map(m => {
            const bot = botMap[m.bot_id];
            return (
              <div key={m.id} className="border border-border rounded-lg p-4 space-y-2" data-testid={`auto-msg-${m.id}`}>
                <div className="flex items-start justify-between gap-3">
                  <div className="flex-1 min-w-0">
                    {m.media_type && <Badge variant="outline" className="text-[10px] mb-1">{m.media_type}</Badge>}
                    <p className="text-sm whitespace-pre-wrap break-words">{m.text || '(media only)'}</p>
                    <div className="flex flex-wrap items-center gap-2 mt-2">
                      <Badge variant="secondary" className="text-[10px]">{INTERVALS.find(i => i.value === m.interval)?.label || m.interval}</Badge>
                      {m.interval !== 'hourly' && <Badge variant="outline" className="text-[10px] font-mono">{m.time}</Badge>}
                      <Badge variant="outline" className="text-[10px]">{m.target === 'active' ? 'Active Only' : 'All Users'}</Badge>
                      {m.inline_buttons?.length > 0 && <Badge variant="outline" className="text-[10px]">{m.inline_buttons.length} btn(s)</Badge>}
                      {bot && <span className="text-[10px] text-primary/70 font-mono">@{bot.bot_username}</span>}
                    </div>
                    {m.last_sent && <p className="text-[10px] text-muted-foreground mt-1">Last: {m.last_sent.replace('T', ' ').slice(0, 16)} ({m.last_sent_count || 0} users)</p>}
                  </div>
                  <div className="flex items-center gap-1 shrink-0">
                    <Badge variant={m.is_active ? 'default' : 'destructive'} className="text-[10px]">{m.is_active ? 'Active' : 'Paused'}</Badge>
                    <Button variant="ghost" size="sm" onClick={() => toggleActive(m)}>{m.is_active ? <Pause size={14} /> : <Play size={14} />}</Button>
                    <Button variant="ghost" size="sm" onClick={() => openEdit(m)}><PencilSimple size={14} /></Button>
                    <Button variant="ghost" size="sm" onClick={() => deleteMsg(m.id)} className="text-destructive"><Trash size={14} /></Button>
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      )}

      <Dialog open={addOpen} onOpenChange={setAddOpen}>
        <DialogContent className="max-h-[85vh] overflow-y-auto">
          <DialogHeader><DialogTitle>{editId ? 'Edit Auto-Message' : 'New Auto-Message'}</DialogTitle></DialogHeader>
          <form onSubmit={handleSave} className="space-y-4">
            {/* Media upload (only for new, not edit) */}
            {!editId && (
              <div className="space-y-1.5">
                <Label>Media (Optional)</Label>
                <input ref={fileRef} type="file" className="hidden" accept="image/*,video/*,.gif" onChange={e => { if (e.target.files[0]) setMediaFile(e.target.files[0]); e.target.value = ''; }} />
                {mediaFile ? (
                  <div className="flex items-center gap-2 border border-border rounded px-3 py-2">
                    {mediaFile.type?.startsWith('image') ? <Image size={16} className="text-blue-500" /> : <VideoCamera size={16} className="text-purple-500" />}
                    <span className="text-sm truncate flex-1">{mediaFile.name}</span>
                    <button type="button" onClick={() => setMediaFile(null)} className="text-destructive"><X size={14} /></button>
                  </div>
                ) : (
                  <div className="flex gap-2">
                    <Button type="button" variant="outline" size="sm" className="gap-1" onClick={() => { fileRef.current.accept = 'image/*'; fileRef.current?.click(); }}><Image size={14} className="text-blue-500" /> Image</Button>
                    <Button type="button" variant="outline" size="sm" className="gap-1" onClick={() => { fileRef.current.accept = 'video/*'; fileRef.current?.click(); }}><VideoCamera size={14} className="text-purple-500" /> Video</Button>
                  </div>
                )}
              </div>
            )}
            <div className="space-y-1.5">
              <Label>Message Text</Label>
              <Textarea value={text} onChange={e => setText(e.target.value)} placeholder="Message to send..." rows={3} data-testid="auto-msg-text" />
            </div>
            {/* Inline buttons */}
            <div className="space-y-1.5">
              <div className="flex items-center justify-between">
                <Label>Inline Buttons</Label>
                <Button type="button" variant="outline" size="sm" className="h-7 text-xs gap-1" onClick={() => setButtons([...buttons, { title: '', url: '' }])}>
                  <Link size={12} /> Add Button
                </Button>
              </div>
              {buttons.map((btn, i) => (
                <div key={i} className="flex gap-2 items-center">
                  <Input value={btn.title} onChange={e => { const b = [...buttons]; b[i].title = e.target.value; setButtons(b); }} placeholder="Title" className="flex-1" />
                  <Input value={btn.url} onChange={e => { const b = [...buttons]; b[i].url = e.target.value; setButtons(b); }} placeholder="https://..." className="flex-1" />
                  <button type="button" onClick={() => setButtons(buttons.filter((_, j) => j !== i))} className="text-destructive"><Trash size={14} /></button>
                </div>
              ))}
              <div className="flex items-center justify-between pt-2 border-t border-border">
                <div><Label className="text-xs">Show &ldquo;Please click below the Buttons :&rdquo; label</Label><p className="text-[10px] text-muted-foreground">When OFF, only buttons are sent</p></div>
                <Switch data-testid="auto-msg-show-btn-label" checked={showBtnLabel} onCheckedChange={setShowBtnLabel} />
              </div>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-1.5">
                <Label>Interval</Label>
                <select value={interval} onChange={e => setIntervalVal(e.target.value)} className="w-full border border-border rounded-md px-3 py-2 text-sm bg-background" data-testid="auto-msg-interval">
                  {INTERVALS.map(i => <option key={i.value} value={i.value}>{i.label}</option>)}
                </select>
              </div>
              {interval !== 'hourly' && (
                <div className="space-y-1.5">
                  <Label>Time (IST)</Label>
                  <Input type="time" value={time} onChange={e => setTime(e.target.value)} data-testid="auto-msg-time" />
                </div>
              )}
            </div>
            <div className="space-y-1.5">
              <Label>Target Users</Label>
              <select value={target} onChange={e => setTarget(e.target.value)} className="w-full border border-border rounded-md px-3 py-2 text-sm bg-background" data-testid="auto-msg-target">
                {TARGETS.map(t => <option key={t.value} value={t.value}>{t.label}</option>)}
              </select>
            </div>
            <p className="text-[10px] text-muted-foreground">Send order: Media → Text → Inline Buttons (label optional)</p>
            <DialogFooter><Button type="submit" disabled={saving} data-testid="auto-msg-save">{saving ? 'Saving...' : editId ? 'Update' : 'Create'}</Button></DialogFooter>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
}
